/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from 'zod';

// Cloudflare Workers types (external)
export type KVNamespace = any;
export type R2Bucket = any;
export type R2Object = any;
export type D1Database = any;
export type AnalyticsEngineDataset = any;

// External types from other packages
export type ChatMessage = { role: 'system' | 'user' | 'assistant'; content: string };
export type AIProvider = 'openai' | 'anthropic' | 'google' | 'cloudflare' | 'cohere' | 'mistral';
export type VectorEntry = any;
export type VectorSearchResult = any;


// Document types and formats
export type DocumentFormat = 'markdown' | 'html' | 'pdf' | 'txt' | 'docx' | 'csv' | 'json' | 'xml' | 'image' | 'audio' | 'video';
export type DocumentSource = 'file' | 'url' | 'text' | 's3' | 'r2' | 'github' | 'webhook' | 'email' | 'auto';

// Document metadata
export interface DocumentMetadata {
  id: string;
  title?: string;
  author?: string;
  createdAt?: number;
  updatedAt?: number;
  url?: string;
  filePath?: string;
  fileSize?: number;
  format: DocumentFormat;
  source: DocumentSource;
  tags?: string[];
  language?: string;
  custom?: Record<string, any>;
}

// Document schema
export const DocumentSchema = z.object({
  id: z.string(),
  content: z.string(),
  metadata: z.object({
    id: z.string(),
    title: z.string().optional(),
    author: z.string().optional(),
    createdAt: z.number().optional(),
    updatedAt: z.number().optional(),
    url: z.string().optional(),
    filePath: z.string().optional(),
    fileSize: z.number().optional(),
    format: z.enum(['markdown', 'html', 'pdf', 'txt', 'docx', 'csv', 'json', 'xml', 'image', 'audio', 'video']),
    source: z.enum(['file', 'url', 'text', 's3', 'r2', 'github', 'webhook', 'email', 'auto']),
    tags: z.array(z.string()).optional(),
    language: z.string().optional(),
    custom: z.record(z.string(), z.unknown()).optional(),
  }),
});

export type Document = z.infer<typeof DocumentSchema>;

// Chunk types and strategies
export type ChunkingStrategy = 'fixed_size' | 'semantic' | 'recursive' | 'sentence' | 'paragraph' | 'custom';

export interface ChunkingConfig {
  strategy: ChunkingStrategy;
  size: number;
  overlap: number;
  minChunkSize?: number;
  maxChunkSize?: number;
  separators?: string[];
  preserveStructure?: boolean;
  customSplitter?: (text: string, config: ChunkingConfig) => Chunk[];
}

// Chunk metadata
export interface ChunkMetadata {
  chunkId: string;
  documentId: string;
  index: number;
  startIndex: number;
  endIndex: number;
  title?: string;
  section?: string;
  headers?: string[];
  pageNumber?: number;
  custom?: Record<string, any>;
}

// Chunk schema
export const ChunkSchema = z.object({
  id: z.string(),
  content: z.string(),
  tokens: z.number(),
  embedding: z.array(z.number()).optional(),
  metadata: z.object({
    chunkId: z.string(),
    documentId: z.string(),
    index: z.number(),
    startIndex: z.number(),
    endIndex: z.number(),
    title: z.string().optional(),
    section: z.string().optional(),
    headers: z.array(z.string()).optional(),
    pageNumber: z.number().optional(),
    custom: z.record(z.string(), z.unknown()).optional(),
  }),
});

export type Chunk = z.infer<typeof ChunkSchema>;

// Embedding configuration
export interface EmbeddingConfig {
  model: string;
  provider: AIProvider;
  dimensions: number;
  batchSize: number;
  maxRetries: number;
  timeout: number;
}

// RAG pipeline configuration
export const RAGConfigSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  embedding: z.object({
    model: z.string(),
    provider: z.enum(['openai', 'anthropic', 'google', 'cloudflare', 'cohere', 'mistral']),
    dimensions: z.number().default(1536),
    batchSize: z.number().default(100),
    maxRetries: z.number().default(3),
    timeout: z.number().default(30000),
  }),
  chunking: z.object({
    strategy: z.enum(['fixed_size', 'semantic', 'recursive', 'sentence', 'paragraph', 'custom']).default('recursive'),
    size: z.number().default(512),
    overlap: z.number().default(128),
    minChunkSize: z.number().default(50),
    maxChunkSize: z.number().default(2048),
    separators: z.array(z.string()).optional(),
    preserveStructure: z.boolean().default(false),
  }),
  retrieval: z.object({
    topK: z.number().default(5),
    minSimilarity: z.number().default(0.7),
    hybridWeight: z.number().default(0.7), // Weight for vector vs keyword search
    rerankModel: z.string().optional(),
    maxContextTokens: z.number().default(4000),
  }),
  generation: z.object({
    model: z.string(),
    provider: z.enum(['openai', 'anthropic', 'google', 'cloudflare', 'cohere', 'mistral']),
    temperature: z.number().default(0.1),
    maxTokens: z.number().default(1000),
    systemPrompt: z.string().optional(),
    includeSource: z.boolean().default(true),
    streaming: z.boolean().default(false),
  }),
  analytics: z.object({
    enabled: z.boolean().default(true),
    trackQueries: z.boolean().default(true),
    trackPerformance: z.boolean().default(true),
    sampleRate: z.number().default(1.0),
  }),
});

export type RAGConfig = z.infer<typeof RAGConfigSchema>;

// Ingestion request
export const IngestionRequestSchema = z.object({
  source: z.string(), // URL, file path, or text content
  type: z.enum(['file', 'url', 'text', 's3', 'r2', 'github', 'auto', 'webhook', 'email']).default('file'),
  format: z.enum(['markdown', 'html', 'pdf', 'txt', 'docx', 'csv', 'json', 'xml', 'image', 'audio', 'video']).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  recursive: z.boolean().default(false), // For directories/repos
  filters: z.array(z.string()).optional(), // File patterns to include
  excludes: z.array(z.string()).optional(), // File patterns to exclude
});

export type IngestionRequest = z.infer<typeof IngestionRequestSchema>;

// Query request
export const QueryRequestSchema = z.object({
  query: z.string(),
  topK: z.number().default(5),
  minSimilarity: z.number().default(0.7),
  filter: z.record(z.string(), z.unknown()).optional(),
  namespace: z.string().optional(),
  includeSource: z.boolean().default(true),
  streaming: z.boolean().default(false),
  conversationHistory: z.array(z.object({
    role: z.enum(['system', 'user', 'assistant']),
    content: z.string(),
  })).optional(),
  customPrompt: z.string().optional(),
  rerankResults: z.boolean().default(true),
});

export type QueryRequest = z.infer<typeof QueryRequestSchema>;

// Context result
export interface ContextResult {
  chunks: Chunk[];
  sources: DocumentMetadata[];
  totalTokens: number;
  retrievalTime: number;
  relevanceScores: number[];
}

// Query response
export interface QueryResponse {
  answer: string;
  sources: Array<{
    documentId: string;
    chunkId: string;
    title?: string;
    url?: string;
    relevanceScore: number;
    content: string;
  }>;
  context: ContextResult;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  performance: {
    retrievalTime: number;
    generationTime: number;
    totalTime: number;
  };
  conversationId?: string;
  requestId: string;
}

// Streaming query response
export interface QueryStreamChunk {
  type: 'context' | 'answer' | 'source' | 'complete' | 'error';
  data: any;
  requestId: string;
}

// RAG analytics event
export interface RAGAnalyticsEvent {
  type: 'ingestion' | 'query' | 'error' | 'performance';
  timestamp: number;
  pipelineName: string;
  requestId: string;
  userId?: string;
  metadata: {
    // Ingestion events
    documentsCount?: number;
    chunksCount?: number;
    processingTime?: number;
    
    // Query events
    query?: string;
    responseTime?: number;
    retrievalTime?: number;
    generationTime?: number;
    sourcesCount?: number;
    tokensUsed?: number;
    
    // Error events
    error?: string;
    errorCode?: string;
    
    // Performance metrics
    latency?: number;
    throughput?: number;
    cost?: number;
  };
}

// RAG pipeline status
export type RAGPipelineStatus = 'initializing' | 'ready' | 'processing' | 'error' | 'disabled';

export interface RAGPipelineInfo {
  name: string;
  status: RAGPipelineStatus;
  config: RAGConfig;
  stats: {
    documentsCount: number;
    chunksCount: number;
    totalQueries: number;
    avgResponseTime: number;
    lastUsed?: number;
    createdAt: number;
    updatedAt: number;
  };
  error?: string;
}

// Document processing result
export interface ProcessingResult {
  document: Document;
  chunks: Chunk[];
  embeddings: number[][];
  processingTime: number;
  tokenCount: number;
  errors?: string[];
}

// Batch processing result
export interface BatchProcessingResult {
  results: ProcessingResult[];
  totalDocuments: number;
  totalChunks: number;
  totalTokens: number;
  processingTime: number;
  errors: Array<{
    documentId: string;
    error: string;
  }>;
}

// Auto-indexing configuration
export interface AutoIndexingConfig {
  enabled: boolean;
  sources: DocumentSource[];
  patterns: string[];
  excludePatterns: string[];
  maxFileSize: number;
  supportedFormats: DocumentFormat[];
  scheduleCron?: string;
  webhookSecret?: string;
}

// Error types
export class RAGError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public pipelineName?: string,
    public originalError?: Error
  ) {
    super(message);
    this.name = 'RAGError';
  }
}

export class DocumentProcessingError extends RAGError {
  constructor(documentId: string, message: string, originalError?: Error) {
    super(
      `Failed to process document ${documentId}: ${message}`,
      'DOCUMENT_PROCESSING_ERROR',
      422,
      undefined,
      originalError
    );
    this.name = 'DocumentProcessingError';
  }
}

export class EmbeddingError extends RAGError {
  constructor(message: string, originalError?: Error) {
    super(
      `Embedding generation failed: ${message}`,
      'EMBEDDING_ERROR',
      500,
      undefined,
      originalError
    );
    this.name = 'EmbeddingError';
  }
}

export class RetrievalError extends RAGError {
  constructor(message: string, originalError?: Error) {
    super(
      `Context retrieval failed: ${message}`,
      'RETRIEVAL_ERROR',
      500,
      undefined,
      originalError
    );
    this.name = 'RetrievalError';
  }
}

export class GenerationError extends RAGError {
  constructor(message: string, originalError?: Error) {
    super(
      `Answer generation failed: ${message}`,
      'GENERATION_ERROR',
      500,
      undefined,
      originalError
    );
    this.name = 'GenerationError';
  }
}

// Environment interface for Cloudflare Workers
export interface RAGEnv {
  // Dependencies from other services
  VECTOR_KV: KVNamespace;
  VECTOR_BUCKET: R2Bucket;
  VECTOR_DB: D1Database;
  CACHE: KVNamespace;
  ANALYTICS: AnalyticsEngineDataset;

  // RAG-specific storage
  RAG_BUCKET: R2Bucket; // For storing original documents
  RAG_KV: KVNamespace; // For pipeline configs and metadata
  RAG_QUEUE?: any; // Cloudflare Queue for webhook processing

  // Cloudflare AI binding
  AI?: any;
  VECTORIZE?: any; // Cloudflare Vectorize binding

  // AI provider keys (inherited from AI Gateway)
  OPENAI_API_KEY: string;
  ANTHROPIC_API_KEY: string;
  GOOGLE_API_KEY: string;
  COHERE_API_KEY: string;
  MISTRAL_API_KEY: string;

  // Configuration
  RAG_DEFAULT_EMBEDDING_MODEL?: string;
  RAG_DEFAULT_GENERATION_MODEL?: string;
  RAG_MAX_CONTEXT_TOKENS?: string;
  RAG_BATCH_SIZE?: string;
  RAG_AUTO_INDEXING?: string;
  RAG_SECURITY_MODE?: string;
  ENVIRONMENT: string;
  DEBUG?: string;
}